import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

export default function CartPage() {
  const [cart, setCart] = useState({ items: [], total: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (product_id) => {
    await api.post("/cart/remove", { product_id });
    toast.success("Article retiré");
    load();
  };

  const clear = async () => {
    await api.post("/cart/clear");
    toast.success("Panier vidé");
    load();
  };

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-brand-green" /></div>;

  return (
    <div data-testid="cart-page">
      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-green mb-2">Panier</div>
      <h1 className="text-4xl font-black tracking-tighter text-white">
        Votre sélection ({cart.count})
      </h1>

      {cart.items.length === 0 ? (
        <div className="mt-10 border border-brand-blue/40 rounded-sm p-12 text-center" data-testid="cart-empty">
          <ShoppingBag className="h-10 w-10 mx-auto text-brand-green mb-4" />
          <p className="text-brand-gray font-roboto">Votre panier est vide.</p>
          <Link to="/app" className="inline-block mt-6 px-6 h-11 leading-[44px] bg-brand-green text-black font-bold rounded-sm hover:bg-brand-green/90 transition" data-testid="cart-go-shopping">
            Voir le catalogue
          </Link>
        </div>
      ) : (
        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 border border-brand-blue/40 rounded-sm divide-y divide-brand-blue/30">
            {cart.items.map((item) => (
              <div key={item.product_id} className="flex items-center gap-4 p-4" data-testid={`cart-item-${item.product_id}`}>
                <div className="h-20 w-20 rounded-sm bg-brand-navy/40 border border-brand-blue/40 overflow-hidden flex-shrink-0">
                  {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-brand-gray/70 font-bold">{item.brand}</div>
                  <div className="text-white font-bold leading-tight truncate">{item.name}</div>
                  <div className="text-xs text-brand-gray/70 font-roboto mt-1">Qté : {item.quantity} · Grade {item.grade}</div>
                </div>
                <div className="text-right">
                  <div className="font-black text-lg text-white">{item.line_total.toFixed(2)} €</div>
                  <div className="text-xs text-brand-gray/50 font-roboto">{item.price.toFixed(2)} € / unité</div>
                </div>
                <button
                  onClick={() => remove(item.product_id)}
                  className="p-2 text-white/50 hover:text-red-400 transition"
                  data-testid={`remove-item-${item.product_id}`}
                  aria-label="Retirer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <aside className="border border-brand-blue/40 rounded-sm p-6 h-fit sticky top-24" data-testid="cart-summary">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-green mb-2">Résumé</div>
            <div className="flex justify-between text-sm font-roboto text-brand-gray py-2">
              <span>Sous-total</span><span>{cart.total.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-sm font-roboto text-brand-gray py-2 border-b border-brand-blue/30">
              <span>Livraison</span><span className="text-brand-green">Offerte</span>
            </div>
            <div className="flex justify-between items-baseline mt-4">
              <span className="font-bold text-white">Total TTC</span>
              <span className="font-black text-2xl text-white tracking-tight">{cart.total.toFixed(2)} €</span>
            </div>
            <button
              onClick={() => navigate("/app/checkout")}
              data-testid="cart-checkout-button"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 h-12 rounded-sm bg-brand-green text-black font-bold hover:bg-brand-green/90 transition shadow-glow-green"
            >
              Passer commande <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={clear}
              data-testid="cart-clear-button"
              className="mt-3 w-full h-10 rounded-sm border border-brand-blue/60 text-white/70 hover:text-white hover:border-red-500/40 text-xs font-bold uppercase tracking-wider transition"
            >
              Vider le panier
            </button>
          </aside>
        </div>
      )}
    </div>
  );
}
